import moment from "moment";
import "moment/locale/es";
import { cuentaMask, dineroDisabledMask } from "../../Components/Items";
import {
  pdfStylesV2,
  seccionHeadingV2,
  applyPdfColors,
  PDF_COLORS,
  PDF_PAGE_MARGINS,
} from "../../Components/PdfComponents/componentsv2.0/stylesPDFv2";
import { PDFHeaderV2 } from "../../Components/PdfComponents/componentsv2.0/PDFHeader";
import { PDFDataTableV2 } from "../../Components/PdfComponents/componentsv2.0/PDFDataTable";
import { PDFBrandFooterConNumeroV2 } from "../../Components/PdfComponents/componentsv2.0/PDFFooter";
import {
  construirAnaliticas,
  renglonesResumen,
  renglonesRazones,
  formatoVeces,
  formatoPorcentaje,
} from "../balanceGeneralAnaliticas";
// Registra la tipografía Mulish en pdfMake (efecto secundario) y expone su nombre.
import MULISH_FONT from "./mulishFont";
// Registra la variante Mulish SemiBold (600) usada en las etiquetas (efecto secundario).
import "./mulishSemiBoldFont";

moment.locale("es");

// Anexo del Estado de Posición Financiera en diseño moderno: los totales, el análisis
// vertical y las razones financieras, en un documento aparte del balance.

const anexoAnalisisBalancePdfV2 = (values) => {
  const empresa = values.empresa;
  const logoBiwo = values.logoBiwo?.base64Completa;

  // Debe ir antes de pdfStylesV2: muta PDF_COLORS in-place con el color de la empresa.
  applyPdfColors(empresa);

  const analiticas = construirAnaliticas(values);
  const mostrarRegimen = !empresa?.deshabilitarRegimenFiscal;

  // Sin analíticas no hay anexo que armar. La pantalla ya no lo pide en ese caso, pero
  // un doc-definition tiene que devolver algo válido de todos modos.
  if (!analiticas) {
    return {
      content: [{ text: "No hay información de análisis para este corte.", alignment: "center" }],
      styles: pdfStylesV2(empresa),
      defaultStyle: { font: MULISH_FONT },
      pageSize: "LETTER",
      pageMargins: PDF_PAGE_MARGINS,
    };
  }

  const { subtotales } = analiticas;

  const textoFiltrado = (() => {
    if (values.ocultarDatosFiltrado) return null;
    if (values.filtrarProyecto === 1) {
      return {
        label: "Proyectos:",
        value: (values.proyectos || []).map((p) => p.nombre).join(", ") || "N/A",
      };
    }
    if (values.filtrarProyecto === 2 && values.unidadDeNegocio) {
      const udn = values.unidadDeNegocio;
      return {
        label: "Unidad de Negocio:",
        value:
          cuentaMask(
            (udn.nivel1 || "") + (udn.nivel2 || "") + (udn.nivel3 || "") +
            (udn.nivel4 || "") + (udn.nivel5 || "")
          ) + " - " + udn.nombre,
      };
    }
    return { label: "Filtros:", value: "Sin filtrados adicionales" };
  })();

  const headerMeta = [
    { label: "Fecha final:", value: moment(values.fechaFinal).locale("es").format("LL") },
    { label: "Fecha de impresión:", value: moment().locale("es").format("LL") },
    ...(values.nivel ? [{ label: "Nivel de detalle:", value: `Nivel ${values.nivel}` }] : []),
    ...(textoFiltrado ? [textoFiltrado] : []),
  ];

  // El cuadre del balance. La Diferencia debe dar cero; si no, hay cuentas mal
  // clasificadas en el catálogo o pólizas descuadradas, y sale en rojo.
  const cuadre = [
    { etiqueta: "Suma del Activo", valor: subtotales.totalActivo },
    { etiqueta: "Suma del Pasivo y Capital", valor: subtotales.totalPasivoMasCapital },
    { etiqueta: "Diferencia", valor: subtotales.diferencia, alerta: subtotales.diferencia !== 0 },
  ];

  const dd = {
    info: {
      title: "Anexo - Análisis del Balance",
      author: "BIWO ERP",
      subject: "Anexo de análisis del Estado de Posición Financiera",
      creator: "pdfmake",
    },
    content: [
      PDFHeaderV2({
        empresa,
        tituloPrincipal: "Anexo · Análisis del Balance",
        tituloFolio: "Al " + moment(values.fechaFinal).locale("es").format("LL"),
        regimenFiscal: mostrarRegimen,
        // No es un comprobante fiscal: sin esta línea el encabezado no habla de CFDI.
        tipoComprobante: false,
        meta: headerMeta,
        metaLayout: "grid",
      }),

      seccionHeadingV2("Cuadre del balance"),
      PDFDataTableV2({
        widths: ["*", "auto"],
        title: [
          { text: "Concepto", style: "tituloTabla", alignment: "left" },
          { text: "Importe", style: "tituloTabla", alignment: "right" },
        ],
        rows: cuadre.map((renglon) => {
          const base = {
            style: "textoTablaChico",
            bold: true,
            fillColor: PDF_COLORS.tint,
            ...(renglon.alerta ? { color: PDF_COLORS.danger } : {}),
          };
          return [
            { ...base, text: renglon.etiqueta, alignment: "left" },
            { ...base, text: dineroDisabledMask(renglon.valor), alignment: "right", noWrap: true },
          ];
        }),
      }),

      seccionHeadingV2("Integración del balance"),
      PDFDataTableV2({
        widths: ["*", "auto", "auto"],
        title: [
          { text: "Concepto", style: "tituloTabla", alignment: "left" },
          { text: "Importe", style: "tituloTabla", alignment: "right" },
          { text: "% Integración", style: "tituloTabla", alignment: "right" },
        ],
        rows: renglonesResumen(analiticas).map((renglon) => {
          const base = {
            style: "textoTablaChico",
            ...(renglon.fuerte ? { bold: true, fillColor: PDF_COLORS.tint } : {}),
            ...(renglon.alerta ? { color: PDF_COLORS.danger } : {}),
          };
          return [
            { ...base, text: renglon.etiqueta, alignment: "left" },
            {
              ...base,
              text: dineroDisabledMask(analiticas.subtotales[renglon.llave]),
              alignment: "right",
              noWrap: true,
            },
            {
              ...base,
              text: formatoPorcentaje(analiticas.porcentajes[renglon.llave]),
              alignment: "right",
              noWrap: true,
            },
          ];
        }),
      }),

      seccionHeadingV2("Razones financieras"),
      PDFDataTableV2({
        widths: ["auto", "*", "auto"],
        title: [
          { text: "Razón", style: "tituloTabla", alignment: "left" },
          { text: "Fórmula", style: "tituloTabla", alignment: "left" },
          { text: "Resultado", style: "tituloTabla", alignment: "right" },
        ],
        rows: renglonesRazones(analiticas).map((renglon) => {
          let texto;
          if (renglon.formato === "dinero") {
            texto = renglon.valor === null ? "N/A" : dineroDisabledMask(renglon.valor);
          } else if (renglon.formato === "porcentaje") {
            texto = formatoPorcentaje(renglon.valor);
          } else {
            texto = formatoVeces(renglon.valor);
          }
          return [
            { text: renglon.etiqueta, style: "textoTablaChico", bold: true, alignment: "left", noWrap: true },
            { text: renglon.nota, style: "textoChicoData", alignment: "left" },
            { text: texto, style: "textoTablaChico", bold: true, alignment: "right", noWrap: true },
          ];
        }),
      }),

      {
        text: 'Las razones se calculan sobre los saldos al corte indicado. "N/A" significa que el divisor es cero.',
        style: "textoChicoData",
        margin: [0, 10, 0, 0],
      },
    ],
    footer: PDFBrandFooterConNumeroV2(logoBiwo),
    images: {
      logo: empresa.logo.base64Completa,
    },
    styles: pdfStylesV2(empresa),
    defaultStyle: { font: MULISH_FONT },
    pageSize: "LETTER",
    pageMargins: PDF_PAGE_MARGINS,
  };

  return dd;
};

export default anexoAnalisisBalancePdfV2;
